import React from 'react';
import { LinkifiedText } from './LinkifiedText';
import type { Session, SessionSponsorGroup } from '../lib/types';

function logoLinkProps(href: string) {
  if (/^https?:/i.test(href)) {
    return { target: '_blank', rel: 'noreferrer' };
  }
  return {};
}

function sponsorParagraphs(value: string | undefined) {
  return String(value || '')
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function SessionSponsorLogoSummary(props: { groups?: SessionSponsorGroup[] }) {
  const groups = props.groups ?? [];
  if (!groups.length) return null;

  return (
    <div className="mt-3 space-y-2">
      {groups.map((group) => (
        <div key={group.tier} className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{group.tier}</div>
          <div className="mt-2 flex flex-wrap justify-center gap-1.5">
            {group.sponsors.map((sponsor) => (
              <div
                key={`${group.tier}-${sponsor.name}`}
                className="flex h-11 w-[7.5rem] min-w-0 items-center justify-center rounded-lg border border-zinc-200 bg-white px-2 py-1 sm:w-36"
                title={sponsor.name}
              >
                <img src={sponsor.logoSrc} alt={sponsor.name} loading="lazy" className="max-h-8 max-w-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SessionImageStrip(props: { images?: Session['sessionImages'] }) {
  const images = props.images ?? [];
  if (!images.length) return null;

  return (
    <div className="mt-3 grid grid-cols-1 justify-items-center gap-2">
      {images.map((image) => {
        const isFeature = image.size === 'feature';
        const content = (
          <img
            src={image.src}
            alt={image.alt ?? image.name}
            loading="lazy"
            className={[
              'w-full rounded-lg object-contain',
              isFeature ? 'max-h-44 sm:max-h-56' : 'max-h-16 max-w-52',
            ].join(' ')}
          />
        );
        const className = [
          'flex w-full items-center justify-center rounded-xl border border-zinc-200 bg-white px-3 py-2 shadow-sm',
          isFeature ? 'max-w-2xl' : 'max-w-64',
        ].join(' ');

        return image.href ? (
          <a key={`${image.name}-${image.src}`} href={image.href} {...logoLinkProps(image.href)} className={className}>
            {content}
          </a>
        ) : (
          <div key={`${image.name}-${image.src}`} className={className}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

export function SessionSponsorDetailGroups(props: { groups?: SessionSponsorGroup[] }) {
  const groups = props.groups ?? [];
  if (!groups.length) return null;

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.tier} className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{group.tier}</h2>
          <div className={['grid grid-cols-1 gap-3', group.sponsors.length > 1 ? 'lg:grid-cols-2' : ''].join(' ')}>
            {group.sponsors.map((sponsor) => {
              const logo = (
                <img
                  src={sponsor.logoSrc}
                  alt={sponsor.name}
                  loading="lazy"
                  className="max-h-14 max-w-full object-contain"
                />
              );

              return (
                <article key={`${group.tier}-${sponsor.name}`} className="rounded-xl border border-zinc-200 bg-white p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-white px-3 py-2 sm:w-40">
                      {sponsor.href ? (
                        <a href={sponsor.href} {...logoLinkProps(sponsor.href)} className="flex h-full w-full items-center justify-center">
                          {logo}
                        </a>
                      ) : (
                        logo
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-zinc-950">{sponsor.name}</div>
                      <div className="mt-1 space-y-2">
                        {[...sponsorParagraphs(sponsor.summary), ...sponsorParagraphs(sponsor.detail)].map((paragraph, index) => (
                          <p key={index} className="whitespace-pre-line text-sm leading-6 text-zinc-700">
                            <LinkifiedText text={paragraph} />
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
